import { z } from 'zod';
import { UlidSchema, IsoTimestampSchema, TenantIdSchema } from './common.js';

/**
 * Paquete — aggregate para gestión de paquetes y encomiendas en portería.
 * Ciclo de vida: recibido → retirado | devuelto | anulado (todos terminales).
 */

export const EstadoPaqueteSchema = z.enum([
  'recibido',
  'retirado',
  'devuelto',
  'anulado',
]);
export type EstadoPaquete = z.infer<typeof EstadoPaqueteSchema>;

/** PIN de retiro: 6 dígitos numéricos. */
export const PinPaqueteSchema = z.string().regex(/^\d{6}$/, 'PIN debe ser 6 dígitos');

/**
 * Paquete — forma canónica con PIN visible.
 * El PIN se omite en shapes públicos (PaquetePublicSchema).
 */
export const PaqueteSchema = z.object({
  id: UlidSchema,
  tenantId: TenantIdSchema,
  unidadId: UlidSchema,
  unidadNumero: z.string(),
  destinatarioNombre: z.string().min(1).max(200),
  descripcion: z.string().max(500).optional(),
  remitente: z.string().max(200).optional(),
  trackingNumber: z.string().max(200).optional(),
  fotoKeys: z.array(z.string().min(1)).max(5).default([]),
  pin: PinPaqueteSchema,
  estado: EstadoPaqueteSchema,
  registradoPorUserId: z.string(),
  registradoPorNombre: z.string(),
  registradoAt: IsoTimestampSchema,
  retiradoPorNombre: z.string().max(200).optional(),
  retiradoAt: IsoTimestampSchema.optional(),
  retiradoSinPin: z.boolean().optional(),
  retiradoObservaciones: z.string().max(500).optional(),
  devueltoAt: IsoTimestampSchema.optional(),
  devueltoRazon: z.string().max(500).optional(),
  anuladoAt: IsoTimestampSchema.optional(),
  anuladoRazon: z.string().max(500).optional(),
});
export type Paquete = z.infer<typeof PaqueteSchema>;

/**
 * PaquetePublic — forma pública sin PIN (§4 D4).
 * Usada en GET /paquetes, GET /paquetes/{id} (para admin/conserje).
 */
export const PaquetePublicSchema = PaqueteSchema.omit({ pin: true });
export type PaquetePublic = z.infer<typeof PaquetePublicSchema>;

/**
 * RegistrarPaqueteRequest — request para POST /paquetes.
 * El backend genera el PIN; el cliente no lo proporciona.
 */
export const RegistrarPaqueteRequestSchema = z.object({
  unidadId: UlidSchema,
  destinatarioNombre: z.string().min(1).max(200),
  descripcion: z.string().max(500).optional(),
  remitente: z.string().max(200).optional(),
  trackingNumber: z.string().max(200).optional(),
  fotoKeys: z.array(z.string().min(1)).max(5).default([]),
});
export type RegistrarPaqueteRequest = z.infer<typeof RegistrarPaqueteRequestSchema>;

/**
 * RetirarPaqueteRequest — request para POST /paquetes/{id}/retirar.
 * Exactamente uno de `pin` o `sinPin` debe estar presente.
 */
export const RetirarPaqueteRequestSchema = z
  .object({
    retiradoPorNombre: z.string().min(1).max(200),
    pin: PinPaqueteSchema.optional(),
    sinPin: z
      .object({ razon: z.string().min(1).max(500) })
      .optional(),
  })
  .refine((v) => Boolean(v.pin) !== Boolean(v.sinPin), {
    message: 'Debe proveer pin o sinPin, no ambos ni ninguno',
  });
export type RetirarPaqueteRequest = z.infer<typeof RetirarPaqueteRequestSchema>;

/**
 * DevolverPaqueteRequest — request para POST /paquetes/{id}/devolver.
 */
export const DevolverPaqueteRequestSchema = z.object({
  razon: z.string().min(1).max(500),
});
export type DevolverPaqueteRequest = z.infer<typeof DevolverPaqueteRequestSchema>;

/**
 * AnularPaqueteRequest — request para POST /paquetes/{id}/anular.
 */
export const AnularPaqueteRequestSchema = z.object({
  razon: z.string().min(1).max(500),
});
export type AnularPaqueteRequest = z.infer<typeof AnularPaqueteRequestSchema>;

/**
 * GenerarUrlSubidaFotoRequest — request para POST /paquetes/foto-upload-url.
 * Incluye validaciones de content-type y size.
 */
export const GenerarUrlSubidaFotoRequestSchema = z.object({
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  contentLength: z.number().int().positive().max(5 * 1024 * 1024),
});
export type GenerarUrlSubidaFotoRequest = z.infer<typeof GenerarUrlSubidaFotoRequestSchema>;

/**
 * UrlSubidaFotoResponse — response con URL presignada de S3 para PUT.
 */
export const UrlSubidaFotoResponseSchema = z.object({
  uploadUrl: z.url(),
  fotoKey: z.string().min(1),
  expiresInSeconds: z.number().int().positive(),
});
export type UrlSubidaFotoResponse = z.infer<typeof UrlSubidaFotoResponseSchema>;

/**
 * ListPaquetesQuerySchema — query params para GET /paquetes.
 * Permite filtrar por estado, unidad y rango de fechas.
 */
export const ListPaquetesQuerySchema = z.object({
  estado: EstadoPaqueteSchema.optional(),
  unidadId: UlidSchema.optional(),
  desde: IsoTimestampSchema.optional(),
  hasta: IsoTimestampSchema.optional(),
});
export type ListPaquetesQuery = z.infer<typeof ListPaquetesQuerySchema>;
